/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { CircularProgress } from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query/react'
import DataTable from 'components/atoms/DataTable'
import Dropdown from 'components/atoms/Dropdown'
import { formatCalendarDate, formatEasternDate } from 'helpers/dates'
import Checkbox from 'components/atoms/IndeterminateCheckbox'
import Input from 'components/atoms/Input'
import { formalName, fullName } from 'helpers/nameHelper'
import Link from 'components/atoms/Link'
import {
  chargeBadgeStyle,
  getBillingTestTypeLabel,
  getShortChargeIdValue,
  RESPONSIBILITY_LABELS,
  responsibilityColor,
} from 'components/lab/billingCharge/utils'
import { TableWrapper } from 'components/atoms/TableWrapper'
import Toast from 'components/atoms/Toast'
import { StatePill } from 'components/lab/atoms/StatePill'
import routes from 'components/lab/constants/routes'
import { BulkSubmissionReport } from 'components/lab/billingCharge/BulkSubmissionReport'
import { SelectClaimStatusModal } from 'components/lab/billingCharge/SelectClaimStatusModal'
import { NeedsWorkFlag } from 'components/lab/billingNeedsWork/NeedsWorkFlag'
import * as generalHelper from 'helpers/generalHelper'
import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import { useLocation, useNavigate } from 'react-router-dom'
import ReactSelect from 'react-select'
import { useGetBillingChargesQuery, useSubmitBillingChargesMutation, } from 'store/services/billingCharges'
import { useGetLabPhysiciansQuery } from 'store/services/labPhysician'
import { useGetLabPayersQuery } from 'store/services/payer'
import { useLazyGetLabPatientListQuery } from 'store/services/patient'
import { Payer, Physician, SubmitBillingChargesResult } from 'store/types'
import { PayerSearchItem } from 'ts-back-end/src/business/payer/dto/payer.dto'
import { useDebounce } from 'use-debounce'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'
import { filterPayerOption, formatPayerOptionLabel } from 'components/lab/payer/PayerOptionLabel'

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.2rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

const InputWrapper = styled.div`
  flex: 1;
  input,
  select {
    height: 38px;
  }
`

interface Props {
  patientId?: string
  allowSubmissions?: boolean
  claimSubmissionMethod?: 'ELECTRONIC' | 'PAPER'
  bucket?: BucketKey
}

type BucketKey = 'all' | 'pending' | 'submitted' | 'failed' | 'active' | 'paid' | 'cob' | 'crossedOver' | 'rejected'

type ClaimSubmissionMethodFilter = '' | 'ELECTRONIC' | 'PAPER'

const initialSearch = {
  patientFirstName: '',
  patientLastName: '',
  patientId: '',
  patientDob: '',
  orderId: '',
  chargeId: '',
  claimIdentifier: '',
  payerClaimNumber: '',
  primaryPayerId: '',
  secondaryPayerId: '',
  chargeStatuses: [] as string[],
  appointmentDateFrom: '',
  appointmentDateTo: '',
  physicianName: '',
  physicianNpi: '',
  orderTestType: '',
  claimSubmissionMethod: '' as ClaimSubmissionMethodFilter,
}

type PatientOption = {
  value: string
  label: string
}

type PayerOption = {
  value: string
  label: string
  matchedName?: string | null
}

const chargeStatusOptions = [
  { value: 'PENDING_SUBMISSION', label: 'Pending Submission' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'FAILED_SUBMISSION', label: 'Failed Submission' },
  { value: 'NOT_SUBMITTABLE', label: 'Not Submittable' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAID', label: 'Paid' },
]

// The pending queue opens on electronic claims; billers can switch to paper [TES-3218].
const defaultClaimSubmissionMethod = (bucket: BucketKey): ClaimSubmissionMethodFilter =>
  bucket === 'pending' ? 'ELECTRONIC' : ''

const claimSubmissionMethodOptions = [
  { value: 'ELECTRONIC', label: 'Electronic' },
  { value: 'PAPER', label: 'Paper' },
]

const formatStatusLabel = (value?: string | null) => {
  if (!value) return '-'

  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const formatMoney = (value?: number | null) => `$${Number(value || 0).toFixed(2)}`

export const BillingChargesListing = ({
  patientId,
  allowSubmissions = false,
  claimSubmissionMethod,
  bucket = 'pending',
}: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const showQueueControls = allowSubmissions || !!claimSubmissionMethod
  const [search, setSearch] = useState(() => ({
    ...initialSearch,
    claimSubmissionMethod: defaultClaimSubmissionMethod(bucket),
  }))
  const [activeBucket, setActiveBucket] = useState<BucketKey>(bucket)
  const [patientSearchInput, setPatientSearchInput] = useState('')
  const [patientOptions, setPatientOptions] = useState<PatientOption[]>([])
  const [selectedPatientOption, setSelectedPatientOption] = useState<PatientOption | null>(null)
  const [patientOptionsLoading, setPatientOptionsLoading] = useState(false)
  const [primaryPlanSearch, setPrimaryPlanSearch] = useState('')
  const [secondaryPlanSearch, setSecondaryPlanSearch] = useState('')
  const [selectedPrimaryPlanOption, setSelectedPrimaryPlanOption] = useState<PayerOption | null>(null)
  const [selectedSecondaryPlanOption, setSelectedSecondaryPlanOption] = useState<PayerOption | null>(null)
  const [debouncedSearch] = useDebounce(search, 500)
  const [debouncedPatientSearch] = useDebounce(patientSearchInput, 300)
  const [debouncedPrimaryPlanSearch] = useDebounce(primaryPlanSearch, 300)
  const [debouncedSecondaryPlanSearch] = useDebounce(secondaryPlanSearch, 300)
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(0)
  const [sortOptions, setSortOptions] = useState({})
  const [submitBillingCharges] = useSubmitBillingChargesMutation()
  // What the last bulk submit did, until the biller closes it.
  const [submitReport, setSubmitReport] = useState<SubmitBillingChargesResult | null>(null)
  const canSubmitCharge = useLabPermission(UserPermission.SUBMIT_BILLING_CHARGES)
  const [showClaimStatusModal, setShowClaimStatusModal] = useState(false)
  const [getPatients] = useLazyGetLabPatientListQuery()

  useEffect(() => {
    setChargesToSubmit([])
    setCurrentPage(0)
    setActiveBucket(bucket)
  }, [bucket])

  useEffect(() => {
    if (!patientId) {
      return
    }

    setCurrentPage(0)
    setChargesToSubmit([])
    setPatientSearchInput('')
    setPatientOptions([])
    setSelectedPatientOption(null)
    setSearch((prev) => ({
      ...prev,
      patientId: '',
      patientFirstName: '',
      patientLastName: '',
      patientDob: '',
    }))
  }, [patientId])

  useEffect(() => {
    if (bucket !== 'all') {
      setPatientSearchInput('')
      setPatientOptions([])
      setSelectedPatientOption(null)
      setPrimaryPlanSearch('')
      setSecondaryPlanSearch('')
      setSelectedPrimaryPlanOption(null)
      setSelectedSecondaryPlanOption(null)
      setSearch((prev) => ({
        ...prev,
        patientId: '',
        patientDob: '',
        chargeId: '',
        claimIdentifier: '',
        payerClaimNumber: '',
        primaryPayerId: '',
        secondaryPayerId: '',
        chargeStatuses: [],
        appointmentDateFrom: '',
        appointmentDateTo: '',
      }))
    }
  }, [bucket])

  useEffect(() => {
    if (activeBucket !== 'all') {
      return
    }

    setSelectedPrimaryPlanOption(null)
    setSelectedSecondaryPlanOption(null)
    setPrimaryPlanSearch('')
    setSecondaryPlanSearch('')
    setSearch((prev) => ({
      ...prev,
      primaryPayerId: '',
      secondaryPayerId: '',
    }))
  }, [activeBucket, search.patientId])

  useEffect(() => {
    const method = defaultClaimSubmissionMethod(activeBucket)
    setSearch((prev) => (prev.claimSubmissionMethod === method ? prev : { ...prev, claimSubmissionMethod: method }))
  }, [activeBucket])

  const { data: physicianList, isFetching: physiciansLoading } = useGetLabPhysiciansQuery(
    typeof debouncedSearch?.physicianName === 'string' &&
      debouncedSearch?.physicianName?.length >= 2
      ? {
          name: debouncedSearch?.physicianName,
        }
      : skipToken,
  )

  const [chargesToSubmit, setChargesToSubmit] = useState<string[]>([])

  const physicians = useMemo(() => {
    if (!Array.isArray(physicianList)) {
      return []
    }

    return physicianList
      .filter((physician: Physician, index: number, list: Physician[]): boolean => {
        const firstIndex = list.findIndex((p: Physician) => String(p.npi) === String(physician.npi))
        return firstIndex === index
      })
      .sort((a: Physician, b: Physician) => {
        return a.lastName === b.lastName
          ? String(a.firstName).localeCompare(b.firstName)
          : String(a.lastName).localeCompare(b.lastName)
      })
  }, [physicianList])

  const physicianOptions = useMemo(
    () => physicians?.map((p) => ({ value: p.npi, label: formalName(p.firstName, p.lastName) })),
    [physicians],
  )

  const { data: primaryPlanList, isFetching: primaryPlansLoading } = useGetLabPayersQuery(
    activeBucket === 'all' && debouncedPrimaryPlanSearch.length >= 2
      ? {
          active: true,
          name: debouncedPrimaryPlanSearch,
          ...(search.patientId ? { patientId: Number(search.patientId) } : {}),
        }
      : skipToken,
  )

  const { data: secondaryPlanList, isFetching: secondaryPlansLoading } = useGetLabPayersQuery(
    activeBucket === 'all' && debouncedSecondaryPlanSearch.length >= 2
      ? {
          active: true,
          name: debouncedSecondaryPlanSearch,
          ...(search.patientId ? { patientId: Number(search.patientId) } : {}),
        }
      : skipToken,
  )

  const mapPayerOption = (payer: PayerSearchItem): PayerOption => ({
    value: String(payer.payerId),
    label: payer.name,
    matchedName: payer.matchedName,
  })

  const primaryPlanOptions = useMemo(() => {
    const options = (primaryPlanList || []).map(mapPayerOption)

    if (selectedPrimaryPlanOption && !options.some((option) => option.value === selectedPrimaryPlanOption.value)) {
      return [selectedPrimaryPlanOption, ...options]
    }

    return options
  }, [primaryPlanList, selectedPrimaryPlanOption])

  const secondaryPlanOptions = useMemo(() => {
    const options = (secondaryPlanList || []).map(mapPayerOption)

    if (selectedSecondaryPlanOption && !options.some((option) => option.value === selectedSecondaryPlanOption.value)) {
      return [selectedSecondaryPlanOption, ...options]
    }

    return options
  }, [secondaryPlanList, selectedSecondaryPlanOption])

  useEffect(() => {
    if (activeBucket !== 'all') {
      return
    }

    const trimmedSearch = debouncedPatientSearch.trim()

    if (!trimmedSearch) {
      setPatientOptions(selectedPatientOption ? [selectedPatientOption] : [])
      setPatientOptionsLoading(false)
      return
    }

    let isActive = true

    const loadPatients = async () => {
      setPatientOptionsLoading(true)

      try {
        const normalizedSearch = trimmedSearch.replace(/^#/, '').trim()
        const requestSize = 10
        const results = new Map<string, PatientOption>()

        const pushPatients = (items: Array<{ id: number | string; firstName?: string; lastName?: string }>) => {
          items.forEach((patient) => {
            results.set(String(patient.id), {
              value: String(patient.id),
              label: `#${patient.id} ${fullName(patient.firstName, patient.lastName)}`.trim(),
            })
          })
        }

        if (/^\d+$/.test(normalizedSearch)) {
          const response = await getPatients({ id: normalizedSearch, size: requestSize }).unwrap()
          pushPatients(response.items || [])
        } else if (normalizedSearch.includes(' ')) {
          const [firstName, ...rest] = normalizedSearch.split(/\s+/)
          const lastName = rest.join(' ')
          const response = await getPatients({ firstName, lastName, size: requestSize }).unwrap()
          pushPatients(response.items || [])
        } else {
          const [firstNameResponse, lastNameResponse] = await Promise.all([
            getPatients({ firstName: normalizedSearch, size: requestSize }).unwrap(),
            getPatients({ lastName: normalizedSearch, size: requestSize }).unwrap(),
          ])

          pushPatients(firstNameResponse.items || [])
          pushPatients(lastNameResponse.items || [])
        }

        if (!isActive) {
          return
        }

        const nextOptions = Array.from(results.values())
        if (selectedPatientOption && !results.has(selectedPatientOption.value)) {
          nextOptions.unshift(selectedPatientOption)
        }
        setPatientOptions(nextOptions)
      } catch {
        if (isActive) {
          setPatientOptions(selectedPatientOption ? [selectedPatientOption] : [])
        }
      } finally {
        if (isActive) {
          setPatientOptionsLoading(false)
        }
      }
    }

    void loadPatients()

    return () => {
      isActive = false
    }
  }, [activeBucket, debouncedPatientSearch, getPatients, selectedPatientOption])

  const { physicianName, ...searchParams } = debouncedSearch

  const bucketFilters = useMemo(() => {
    switch (activeBucket) {
      case 'all':
        return {}
      case 'pending':
        return {
          submissionStatus: ['PENDING_SUBMISSION'],
        }
      case 'submitted':
        return {
          submissionStatus: ['SUBMITTED'],
        }
      case 'failed':
        return {
          submissionStatus: ['FAILED_SUBMISSION'],
        }
      case 'paid':
        return {
          balanceStatus: ['PAID'],
        }
      case 'cob':
        return {
          cobQueue: 'PENDING',
        }
      case 'crossedOver':
        return {
          cobQueue: 'CROSSED_OVER',
        }
      case 'rejected':
        return {
          optumClaimStatus: 'REJECTED',
        }
      case 'active':
      default:
        return {
          balanceStatus: ['ACTIVE'],
        }
    }
  }, [activeBucket])

  const billingChargeQuery = useMemo(() => {
    const rawQuery = {
      page: currentPage,
      size: pageSize,
      ...searchParams,
      ...(searchParams.patientId && !patientId ? { patientId: Number(searchParams.patientId) } : {}),
      ...(searchParams.primaryPayerId
        ? { primaryPayerId: Number(searchParams.primaryPayerId) }
        : {}),
      ...(searchParams.secondaryPayerId
        ? { secondaryPayerId: Number(searchParams.secondaryPayerId) }
        : {}),
      ...(searchParams.chargeStatuses?.length ? { chargeStatuses: searchParams.chargeStatuses } : {}),
      ...bucketFilters,
      sortField: sortOptions?.id,
      sortOrder: sortOptions?.desc ? 'desc' : 'asc',
      claimSubmissionMethod: claimSubmissionMethod || searchParams.claimSubmissionMethod,
      ...(patientId ? { patientId: Number(patientId) } : {}),
    }

    return generalHelper.cleanObj(rawQuery, true)
  }, [bucketFilters, claimSubmissionMethod, currentPage, pageSize, patientId, searchParams, sortOptions])

  const {
    data: charges,
    isLoading: chargesLoading,
    refetch,
  } = useGetBillingChargesQuery(billingChargeQuery)

  const selectedCharges = useMemo(
    () => (charges?.items || []).filter((charge) => chargesToSubmit.includes(charge.id)),
    [charges?.items, chargesToSubmit],
  )
  const hasSelectedPaperCharges = selectedCharges.some(
    (charge) => charge.claimSubmissionMethod === 'PAPER',
  )
  const hasSelectedElectronicCharges = selectedCharges.some(
    (charge) => charge.claimSubmissionMethod === 'ELECTRONIC',
  )
  const bulkSubmitLabel = hasSelectedPaperCharges && hasSelectedElectronicCharges
    ? 'Submit / Mark Submitted'
    : hasSelectedPaperCharges
      ? 'Mark as Submitted'
      : 'Submit to Optum'
  const canSubmitFromBucket =
    allowSubmissions && canSubmitCharge && (activeBucket === 'pending' || activeBucket === 'failed')
  const isCobBucket = activeBucket === 'cob' || activeBucket === 'crossedOver'
  const isRejectedBucket = activeBucket === 'rejected'

  // The rejection on the row, whichever claim rank carries it; the charge is
  // in this bucket because at least one does and it is flagged for review.
  const openRejection = (row: any): { rank: string; snapshot: any } | null => {
    const ranks: Array<[string, any]> = [
      ['Primary', row.optumClaimStatus],
      ['Secondary', row.secondaryOptumClaimStatus],
      ['Tertiary', row.tertiaryOptumClaimStatus],
    ]
    const hit = ranks.find(([, snapshot]) => snapshot?.status === 'REJECTED')

    return hit ? { rank: hit[0], snapshot: hit[1] } : null
  }

  const chargeLocation = (locationCode: string) => {
    const strLocationCode = String(locationCode)
    if (strLocationCode === '11') {
      return 'Office (11)'
    } else if (strLocationCode === '12') {
      return 'Home (12)'
    } else if (strLocationCode === '81') {
      return 'Independent Laboratory (81)'
    } else {
      return `Unknown (${strLocationCode})`
    }
  }

  const tableColumns = [
    ...(canSubmitFromBucket
      ? [
          {
            id: 'select',
            accessor: 'select',
            disableSortBy: true,
            Header: ({ rows }) => {
              const rowIds = rows.map(({ original }: any) => original.id)
              const allSelected = rowIds.length > 0 && rowIds.every((id: string) => chargesToSubmit.includes(id))
              const someSelected = rowIds.some((id: string) => chargesToSubmit.includes(id))

              return (
                <Checkbox
                  css={{ marginLeft: '5px' }}
                  indeterminate={someSelected && !allSelected}
                  checked={allSelected}
                  onChange={() => setChargesToSubmit(allSelected ? [] : rowIds)}
                />
              )
            },
            Cell: ({ row }: any) => (
              <div className="pl-1 pr-2">
                <Checkbox
                  checked={chargesToSubmit.includes(row.original.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() =>
                    setChargesToSubmit((prev) => {
                      const chargeId = row.original.id
                      if (prev.includes(chargeId)) {
                        return prev.filter((id) => id !== chargeId)
                      }
                      return [...prev, chargeId]
                    })
                  }
                />
              </div>
            ),
          },
        ]
      : []),
    {
      Header: 'Patient',
      accessor: 'patientFirstName',
      Cell: (props: any) => {
        const row = props.row.original

        return (
          <Link
            className="d-flex align-items-center"
            to={`${routes.index}${routes.patient.root}/${row.patientId}`}
            onClick={(e) => e.stopPropagation()}
            style={{ textDecoration: 'none' }}>
            <span className="border border-primary rounded p-2 mr-1 d-flex align-items-center">
              {row.priorityLevel === 1 && <i className="fas fa-exclamation-circle mr-1" style={{ color: 'red' }} />}
              {`#${row.patientId}`}
            </span>
            <span className="ml-1 text-body" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {`${row.patientFirstName} ${row.patientLastName}`}
            </span>
          </Link>
        )
      },
    },
    {
      Header: 'Charge ID',
      accessor: 'id',
      Cell: (props: any) => {
        const row = props.row.original

        return (
          <Link
            to={`${routes.index}${routes.patient.root}/${row.patientId}${routes.billing.root}${routes.billing.charge}${routes.billing.edit}${row.id}`}
            style={{ textDecoration: 'none' }}>
            <span style={chargeBadgeStyle}>{getShortChargeIdValue(row.id)}</span>
            <NeedsWorkFlag count={row.openNeedsWorkCount} reasons={row.openNeedsWorkReasons} />
          </Link>
        )
      },
    },
    {
      Header: 'Order ID',
      accessor: 'orderId',
      Cell: (props: any) => props.row.original.orderId || '-',
    },
    {
      Header: 'Member ID',
      accessor: 'memberId',
      Cell: (props: any) => props.row.original.memberId || '-',
    },
    {
      Header: 'Order Test Type',
      accessor: 'orderTestType',
      Cell: (props: any) => getBillingTestTypeLabel(props.row.original.orderTestType) || '-',
    },
    {
      Header: 'Appointment',
      accessor: 'appointmentDate',
      Cell: (props: any) => {
        const row = props.row.original
        return formatCalendarDate(row.appointmentDate)
      },
    },
    {
      Header: 'Submission',
      accessor: 'submissionStatus',
      Cell: (props: any) => formatStatusLabel(props.row.original.submissionStatus),
    },
    {
      Header: 'Balance Status',
      accessor: 'balanceStatus',
      Cell: (props: any) => formatStatusLabel(props.row.original.balanceStatus),
    },
    ...(isCobBucket
      ? [
          {
            Header: 'Responsible Plan',
            accessor: 'responsiblePayerName',
            disableSortBy: true,
            Cell: (props: any) => {
              const row = props.row.original

              return (
                <div className="d-flex flex-column align-items-start">
                  <span>{row.responsiblePayerName || 'Patient'}</span>
                  {row.responsibleState && (
                    <StatePill
                      color={responsibilityColor(row.responsibleState)}
                      label={RESPONSIBILITY_LABELS[row.responsibleState] || row.responsibleState}
                      tooltip={row.responsibleState === 'CROSSED_OVER'
                        ? `Forwarded to ${row.responsiblePayerName || 'the next payer'}`
                        : undefined}
                    />
                  )}
                </div>
              )
            },
          },
          {
            Header: 'Transfer Balance',
            accessor: 'transferAmount',
            disableSortBy: true,
            Cell: (props: any) => `$${Number(props.row.original.transferAmount || 0).toFixed(2)}`,
          },
        ]
      : []),
    ...(isRejectedBucket
      ? [
          {
            Header: 'Rejection',
            accessor: 'optumClaimStatus',
            disableSortBy: true,
            Cell: (props: any) => {
              const rejection = openRejection(props.row.original)

              if (!rejection) {
                return '-'
              }

              const { rank, snapshot } = rejection
              const codes = [snapshot.categoryCode, snapshot.statusCode].filter(Boolean).join(':')
              const reason = snapshot.messages?.[0] || snapshot.statusDescription || snapshot.categoryDescription || ''

              return (
                <div
                  className="d-flex flex-column align-items-start"
                  title={[snapshot.categoryDescription, snapshot.statusDescription, ...(snapshot.messages || [])]
                    .filter(Boolean)
                    .join('\n')}>
                  <span css={{ fontWeight: 600, color: '#c0392b' }}>
                    {rank} · {snapshot.payerName || '—'} · {codes}
                  </span>
                  <span css={{ fontSize: 12, maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {reason}
                  </span>
                  <span className="text-muted" css={{ fontSize: 12 }}>
                    {formatCalendarDate(snapshot.effectiveDate, '')}
                    {snapshot.payerClaimNumber ? ` · Claim Number ${snapshot.payerClaimNumber}` : ''}
                  </span>
                </div>
              )
            },
          },
        ]
      : []),
    {
      Header: 'Submission Type',
      accessor: 'claimSubmissionMethod',
      Cell: (props: any) => {
        const row = props.row.original

        if (row.type === 'QUICK' || row.submissionStatus === 'NOT_SUBMITTABLE') {
          return 'Patient Responsibility'
        }

        if (row.claimSubmissionMethod === 'PAPER') {
          return 'Paper'
        }

        if (row.claimSubmissionMethod === 'ELECTRONIC') {
          return 'Electronic'
        }

        return '-'
      },
    },
    {
      Header: 'Patient Balance',
      accessor: 'currentPatientBalance',
      Cell: (props: any) => formatMoney(props.row.original.currentPatientBalance),
    },
    {
      Header: 'Plan Balance',
      accessor: 'currentPlanBalance',
      Cell: (props: any) => formatMoney(props.row.original.currentPlanBalance),
    },
    {
      Header: 'Applied Patient',
      accessor: 'appliedPatientAmount',
      Cell: (props: any) => formatMoney(props.row.original.appliedPatientAmount),
    },
    {
      Header: 'Applied Plan',
      accessor: 'appliedPlanAmount',
      Cell: (props: any) => formatMoney(props.row.original.appliedPlanAmount),
    },
    {
      Header: 'Write-off',
      accessor: 'writeOffAmount',
      Cell: (props: any) => formatMoney(props.row.original.writeOffAmount),
    },
    {
      Header: 'Created',
      accessor: 'createdAt',
      Cell: (props: any) => {
        const row = props.row.original
        return formatEasternDate(row.createdAt)
      },
    },
    {
      Header: 'Place of Service',
      accessor: 'placeOfService',
      Cell: (props: any) => {
        const row = props.row.original

        return row.placeOfService ? chargeLocation(row.placeOfService) : '-'
      },
    },
  ]

  const changeSearch = (field: string, value: string | boolean | string[] | null) => {
    setCurrentPage(0)
    setSearch((p) => ({ ...p, [field]: value }))
  }

  const clearSearch = () => {
    setCurrentPage(0)
    setSearch({ ...initialSearch, claimSubmissionMethod: defaultClaimSubmissionMethod(activeBucket) })
    setPatientSearchInput('')
    setPatientOptions([])
    setSelectedPatientOption(null)
    setPrimaryPlanSearch('')
    setSecondaryPlanSearch('')
    setSelectedPrimaryPlanOption(null)
    setSelectedSecondaryPlanOption(null)
  }

  const selectBucket = (bucket: BucketKey) => {
    const params = new URLSearchParams(location.search)
    const bucketToQueryValue: Record<BucketKey, string> = {
      all: routes.order.billing.billingChargesAll.bucket,
      pending: routes.order.billing.billingChargesPendingSubmission.bucket,
      submitted: routes.order.billing.billingChargesSubmitted.bucket,
      failed: routes.order.billing.billingChargesFailedSubmission.bucket,
      active: routes.order.billing.billingChargesActive.bucket,
      paid: routes.order.billing.billingChargesPaid.bucket,
      cob: routes.order.billing.billingChargesCobPending.bucket,
      crossedOver: routes.order.billing.billingChargesCrossedOver.bucket,
      rejected: routes.order.billing.billingChargesRejected.bucket,
    }

    params.set('bucket', bucketToQueryValue[bucket])

    navigate({
      pathname: location.pathname,
      search: params.toString(),
    })

    setCurrentPage(0)
    setChargesToSubmit([])
    setActiveBucket(bucket)
  }

  return (
    <div
      css={{
        width: '100%',
      }}>
      {!showQueueControls && patientId && (
        <div className="d-flex justify-content-center mb-3">
          <ButtonGroup aria-label="charge-balance-filter">
            {(
              [
                ['all', 'All Charges'],
                ['active', 'Active'],
                ['paid', 'Paid'],
              ] as Array<[BucketKey, string]>
            ).map(([bucketKey, label]) => (
              <Button
                key={bucketKey}
                variant={activeBucket === bucketKey ? 'primary' : 'outline-primary'}
                active={activeBucket === bucketKey}
                onClick={() => {
                  setCurrentPage(0)
                  setActiveBucket(bucketKey)
                }}>
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </div>
      )}
      {showQueueControls && (
        <>
          <div className="d-flex justify-content-center mb-3">
            <ButtonGroup aria-label="charge-status-filter">
              <Button
                variant={activeBucket === 'all' ? 'primary' : 'outline-primary'}
                active={activeBucket === 'all'}
                onClick={() => selectBucket('all')}>
                All Charges
              </Button>
              <Button
                variant={activeBucket === 'pending' ? 'primary' : 'outline-primary'}
                active={activeBucket === 'pending'}
                onClick={() => selectBucket('pending')}>
                Pending for submission
              </Button>
              <Button
                variant={activeBucket === 'submitted' ? 'primary' : 'outline-primary'}
                active={activeBucket === 'submitted'}
                onClick={() => selectBucket('submitted')}>
                Submitted
              </Button>
              {claimSubmissionMethod !== 'PAPER' && (
                <Button
                  variant={activeBucket === 'failed' ? 'primary' : 'outline-primary'}
                  active={activeBucket === 'failed'}
                  onClick={() => selectBucket('failed')}>
                  Failed to submit
                </Button>
              )}
              <Button
                variant={activeBucket === 'active' ? 'primary' : 'outline-primary'}
                active={activeBucket === 'active'}
                onClick={() => selectBucket('active')}>
                Active
              </Button>
              <Button
                variant={activeBucket === 'paid' ? 'primary' : 'outline-primary'}
                active={activeBucket === 'paid'}
                onClick={() => selectBucket('paid')}>
                Paid
              </Button>
              <Button
                variant={activeBucket === 'cob' ? 'primary' : 'outline-primary'}
                active={activeBucket === 'cob'}
                title="Charges whose balance a payer handed to the next payer, with no COB claim sent yet"
                onClick={() => selectBucket('cob')}>
                COB to submit
              </Button>
              <Button
                variant={activeBucket === 'crossedOver' ? 'primary' : 'outline-primary'}
                active={activeBucket === 'crossedOver'}
                title="Charges the payer forwarded to the next payer itself — awaiting their payment"
                onClick={() => selectBucket('crossedOver')}>
                Crossed over
              </Button>
              <Button
                variant={activeBucket === 'rejected' ? 'primary' : 'outline-primary'}
                active={activeBucket === 'rejected'}
                title="Submitted claims that came back rejected — fix and resubmit, or mark the rejection resolved"
                onClick={() => selectBucket('rejected')}>
                Rejected
              </Button>
            </ButtonGroup>
          </div>
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
            }}>
            <ButtonGroup className="me-2" aria-label="First group">
              <Button
                active={search.orderTestType === ''}
                variant="secondary"
                onClick={() => changeSearch('orderTestType', '')}>
                All
              </Button>
              <Button
                active={search.orderTestType === 'POX_COX'}
                variant="secondary"
                onClick={() => changeSearch('orderTestType', 'POX_COX')}>
                POX/COX
              </Button>
              <Button
                active={search.orderTestType === 'HST'}
                variant="secondary"
                onClick={() => changeSearch('orderTestType', 'HST')}>
                HST
              </Button>
              <Button
                active={search.orderTestType === 'PSG'}
                variant="secondary"
                onClick={() => changeSearch('orderTestType', 'PSG')}>
                InLab
              </Button>
            </ButtonGroup>
          </div>
          <SelectClaimStatusModal
            show={showClaimStatusModal}
            submitLabel={bulkSubmitLabel}
            onClose={() => setShowClaimStatusModal(false)}
            onSubmit={async (claimStatus: string) => {
              setShowClaimStatusModal(false)
              try {
                // Every charge that can go out does; the ones that cannot come
                // back named, with their reason, instead of stopping the batch.
                const report = await submitBillingCharges({
                  ids: chargesToSubmit,
                  claimStatus,
                  reportFailures: true,
                }).unwrap()

                setSubmitReport(report)
                // Only the charges that need attention stay selected, ready to
                // fix and resubmit; the ones that went out leave the list.
                setChargesToSubmit(report.failed.map((failure) => failure.chargeId))

                // A quick word that the request went through; the report above
                // the list carries the details.
                const sent = report.submitted.length
                const failed = report.failed.length
                const charges = (count: number) => `${count} ${count === 1 ? 'charge' : 'charges'}`

                if (!failed) {
                  Toast({ type: 'success', label: `${charges(sent)} submitted` })
                } else if (sent) {
                  Toast({ type: 'warning', label: `${charges(sent)} submitted, ${failed} need attention` })
                } else {
                  Toast({ type: 'error', label: `No charges submitted, ${failed} need attention` })
                }
              } catch (e: any) {
                Toast({ type: 'error', label: e?.data?.message || 'Failed to process charges' })
              } finally {
                await refetch()
              }
            }}
          />
          <div className={'d-flex justify-content-end mb-2'}>
            <Button variant="link" onClick={clearSearch}>
              Clear
            </Button>
            {canSubmitFromBucket && (
              <Button
                variant={'primary'}
                disabled={!chargesToSubmit.length}
                onClick={() => {
                  setShowClaimStatusModal(true)
                }}>
                {bulkSubmitLabel}
              </Button>
            )}
            <Dropdown
              initialValue={pageSize}
              onClick={(v) => {
                setCurrentPage(0)
                setPageSize(Number(v))
              }}
              buttonTypeClass="btn-outline-primary ml-2"
              options={[
                { label: '12' },
                { label: '20' },
                { label: '50' },
                { label: '100' },
                { label: '200' },
              ]}
              additionalText="Records"
            />
          </div>
          {submitReport && (
            <BulkSubmissionReport report={submitReport} onClose={() => setSubmitReport(null)} />
          )}
          {activeBucket === 'all' ? (
            <>
              <div className="row mb-3">
                <div className="col" style={{ flex: 1, position: 'relative' }}>
                  <label className="text-gray" htmlFor="patientId">
                    Patient (search by ID/Name)
                  </label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    className="basic-single w-100"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select Patient"
                    name="patientId"
                    options={patientOptions}
                    value={selectedPatientOption}
                    onChange={(item) => {
                      setSelectedPatientOption(item)
                      changeSearch('patientId', item?.value || '')
                      setPatientSearchInput('')
                    }}
                    onInputChange={(value, meta) => {
                      if (meta.action === 'input-change') {
                        setPatientSearchInput(value)
                      }
                    }}
                    inputValue={patientSearchInput}
                  />
                  {patientOptionsLoading && (
                    <div style={{ position: 'absolute', top: 0, right: '10px' }}>
                      <CircularProgress size={20} />
                    </div>
                  )}
                </div>
                <InputWrapper className="col">
                  <Input
                    value={search.patientFirstName}
                    placeholder="Patient First Name"
                    label="Patient First Name"
                    name="patientFirstName"
                    handleChange={(e) => changeSearch('patientFirstName', e.target.value)}
                  />
                </InputWrapper>
                <InputWrapper className="col">
                  <Input
                    value={search.patientLastName}
                    placeholder="Patient Last Name"
                    label="Patient Last Name"
                    name="patientLastName"
                    handleChange={(e) => changeSearch('patientLastName', e.target.value)}
                  />
                </InputWrapper>
                <InputWrapper className="col">
                  <Input
                    type="date"
                    value={search.patientDob}
                    label="DOB"
                    name="patientDob"
                    handleChange={(e) => changeSearch('patientDob', e.target.value)}
                  />
                </InputWrapper>
              </div>
              <div className="row mb-3">
                <InputWrapper className="col">
                  <Input
                    value={search.chargeId}
                    placeholder="First characters of the ID"
                    label="Charge ID"
                    name="chargeId"
                    handleChange={(e) => changeSearch('chargeId', e.target.value)}
                  />
                </InputWrapper>
                <InputWrapper className="col">
                  <Input
                    value={search.claimIdentifier}
                    placeholder="e.g. M7QO2Y5JMB"
                    label="Claim Identifier"
                    name="claimIdentifier"
                    handleChange={(e) => changeSearch('claimIdentifier', e.target.value)}
                  />
                </InputWrapper>
                <InputWrapper className="col">
                  <Input
                    value={search.payerClaimNumber}
                    placeholder="The payer's number (ICN)"
                    label="Claim Number"
                    name="payerClaimNumber"
                    handleChange={(e) => changeSearch('payerClaimNumber', e.target.value)}
                  />
                </InputWrapper>
              </div>
              <div className="row mb-3">
                <div className="col" style={{ flex: 1, position: 'relative' }}>
                  <label className="text-gray" htmlFor="primaryPayerId">
                    Primary Plan
                  </label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    className="basic-single w-100"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select Primary Plan"
                    name="primaryPayerId"
                    options={primaryPlanOptions}
                    formatOptionLabel={formatPayerOptionLabel}
                    filterOption={filterPayerOption}
                    value={selectedPrimaryPlanOption}
                    onChange={(item) => {
                      setSelectedPrimaryPlanOption(item)
                      changeSearch('primaryPayerId', item?.value || '')
                      setPrimaryPlanSearch('')
                    }}
                    onInputChange={(value, meta) => {
                      if (meta.action === 'input-change') {
                        setPrimaryPlanSearch(value)
                      }
                    }}
                    inputValue={primaryPlanSearch}
                  />
                  {primaryPlansLoading && (
                    <div style={{ position: 'absolute', top: 0, right: '10px' }}>
                      <CircularProgress size={20} />
                    </div>
                  )}
                </div>
                <div className="col" style={{ flex: 1, position: 'relative' }}>
                  <label className="text-gray" htmlFor="secondaryPayerId">
                    Secondary Plan
                  </label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    className="basic-single w-100"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select Secondary Plan"
                    name="secondaryPayerId"
                    options={secondaryPlanOptions}
                    formatOptionLabel={formatPayerOptionLabel}
                    filterOption={filterPayerOption}
                    value={selectedSecondaryPlanOption}
                    onChange={(item) => {
                      setSelectedSecondaryPlanOption(item)
                      changeSearch('secondaryPayerId', item?.value || '')
                      setSecondaryPlanSearch('')
                    }}
                    onInputChange={(value, meta) => {
                      if (meta.action === 'input-change') {
                        setSecondaryPlanSearch(value)
                      }
                    }}
                    inputValue={secondaryPlanSearch}
                  />
                  {secondaryPlansLoading && (
                    <div style={{ position: 'absolute', top: 0, right: '10px' }}>
                      <CircularProgress size={20} />
                    </div>
                  )}
                </div>
                <div className="col" style={{ flex: 1 }}>
                  <label className="text-gray" htmlFor="chargeStatuses">
                    Charge Statuses
                  </label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    className="basic-single w-100"
                    classNamePrefix="select"
                    isMulti
                    isSearchable
                    placeholder="Select Charge Statuses"
                    name="chargeStatuses"
                    options={chargeStatusOptions}
                    value={chargeStatusOptions.filter((option) => search.chargeStatuses.includes(option.value))}
                    onChange={(items) => {
                      changeSearch('chargeStatuses', items?.map((item) => item.value) || [])
                    }}
                  />
                </div>
              </div>
              <div className="row mb-4">
                <InputWrapper className="col">
                  <Input
                    value={search.orderId}
                    placeholder="Order ID"
                    label="Order ID"
                    name="orderId"
                    handleChange={(e) => changeSearch('orderId', e.target.value)}
                  />
                </InputWrapper>
                <InputWrapper className="col">
                  <Input
                    type="date"
                    value={search.appointmentDateFrom}
                    label="DOS From"
                    name="appointmentDateFrom"
                    handleChange={(e) => changeSearch('appointmentDateFrom', e.target.value)}
                  />
                </InputWrapper>
                <InputWrapper className="col">
                  <Input
                    type="date"
                    value={search.appointmentDateTo}
                    label="DOS To"
                    name="appointmentDateTo"
                    handleChange={(e) => changeSearch('appointmentDateTo', e.target.value)}
                  />
                </InputWrapper>
                <div className="col" style={{ flex: 1, position: 'relative' }}>
                  <label className="text-gray" htmlFor="physicianNpi">
                    Physician
                  </label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    className="basic-single w-100 mr-3"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select Physician"
                    name="physicianNpi"
                    onChange={(item) => changeSearch('physicianNpi', item?.value)}
                    options={physicianOptions}
                    value={physicianOptions?.find((p) => p.value === search.physicianNpi) ?? null}
                    onInputChange={(v) => changeSearch('physicianName', v)}
                    inputValue={search.physicianName}
                  />
                  {physiciansLoading && (
                    <div style={{ position: 'absolute', top: 0, right: '10px' }}>
                      <CircularProgress size={20} />
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="row mb-4">
              <InputWrapper className="col">
                <Input
                  value={search.orderId}
                  placeholder="Order ID"
                  label="Order ID"
                  name="orderId"
                  handleChange={(e) => changeSearch('orderId', e.target.value)}
                />
              </InputWrapper>
              <InputWrapper className="col">
                <Input
                  value={search.patientFirstName}
                  placeholder="Patient First Name"
                  label="Patient First Name"
                  name="patientFirstName"
                  handleChange={(e) => changeSearch('patientFirstName', e.target.value)}
                />
              </InputWrapper>
              <InputWrapper className="col" style={{ flex: 1 }}>
                <Input
                  value={search.patientLastName}
                  placeholder="Patient Last Name"
                  label="Patient Last Name"
                  name="patientLastName"
                  handleChange={(e) => changeSearch('patientLastName', e.target.value)}
                />
              </InputWrapper>
              {activeBucket === 'pending' && !claimSubmissionMethod && (
                <div className="col" style={{ flex: 1 }}>
                  <label className="text-gray" htmlFor="claimSubmissionMethod">
                    Submission Type
                  </label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    className="basic-single w-100"
                    classNamePrefix="select"
                    isClearable
                    placeholder="Select Submission Type"
                    name="claimSubmissionMethod"
                    options={claimSubmissionMethodOptions}
                    value={
                      claimSubmissionMethodOptions.find(
                        (option) => option.value === search.claimSubmissionMethod,
                      ) ?? null
                    }
                    onChange={(item) => changeSearch('claimSubmissionMethod', item?.value || '')}
                  />
                </div>
              )}
              <div className="col" style={{ flex: 1, position: 'relative' }}>
                <label className="text-gray" htmlFor="physicianNpi">
                  Physician
                </label>
                <ReactSelect
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                  className="basic-single w-100 mr-3"
                  classNamePrefix="select"
                  isClearable
                  isSearchable
                  placeholder="Select Physician"
                  name="physicianNpi"
                  onChange={(item) => changeSearch('physicianNpi', item?.value)}
                  options={physicianOptions}
                  value={physicianOptions?.find((p) => p.value === search.physicianNpi) ?? null}
                  onInputChange={(v) => changeSearch('physicianName', v)}
                  inputValue={search.physicianName}
                />
                {physiciansLoading && (
                  <div style={{ position: 'absolute', top: 0, right: '10px' }}>
                    <CircularProgress size={20} />
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      <StyledTableWrapper
        className="card-body row"
        style={{
          overflowY: !charges?.items?.length || charges?.items?.length < 10 ? 'unset' : 'auto',
        }}>
        <DataTable
          data={charges}
          tableKey={`lab_patient_billing_charges_${patientId || 'all'}`}
          status={chargesLoading}
          tableColumns={tableColumns}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          setSortOptions={setSortOptions}
          entityName={'charges'}
          onRowClick={(row) => {
            navigate(
              `${routes.index}${routes.patient.root}/${row.original.patientId}${routes.billing.root}${routes.billing.charge}${routes.billing.edit}${row.original.id}`,
            )
          }}
        />
      </StyledTableWrapper>
    </div>
  )
}
